import { Show, createSignal, type JSX } from "solid-js";
import { ChevronDownIcon, ChevronRightIcon } from "./icons.tsx";
import styles from "./Panel.module.scss";

export type PanelProps = {
  title?: string;
  description?: string;
  /** Buttons or pickers that belong to this section's header. */
  actions?: JSX.Element;
  /**
   * Folds the body away behind its heading, the way a topic folds on the
   * catalog screen. For a section that grows past the screen and is not what
   * the reader came for. Needs a title, because the heading is the handle.
   */
  collapsible?: boolean;
  /** Opens the section on first render. Only read when `collapsible`. */
  defaultOpen?: boolean;
  /**
   * How many entries the body holds, shown beside the title the way the
   * catalog counts a topic's questions, so a folded section still says
   * whether there is anything behind it.
   */
  count?: number;
  children: JSX.Element;
};

/** The section shell every admin screen is built from: heading, actions, body. */
export const Panel = (props: PanelProps) => {
  const [open, setOpen] = createSignal(props.defaultOpen === true);
  const folded = () => props.collapsible === true && !open();

  return (
    <section class={styles.panel}>
      <Show when={props.title !== undefined || props.actions !== undefined}>
        <header class={styles.header}>
          <div class={styles.headline}>
            <Show when={props.title !== undefined}>
              <h2 class={styles.title}>
                {/*
                  The whole heading is the handle when the section folds, so the
                  target is the words and not just the chevron beside them.
                */}
                <Show when={props.collapsible === true} fallback={props.title}>
                  <button
                    class={styles.disclosure}
                    type="button"
                    aria-expanded={open()}
                    onClick={() => setOpen(!open())}
                  >
                    <Show when={open()} fallback={<ChevronRightIcon />}>
                      <ChevronDownIcon />
                    </Show>
                    {props.title}
                  </button>
                </Show>
                <Show when={props.count !== undefined}>
                  <span class={styles.count}>{props.count}</span>
                </Show>
              </h2>
            </Show>
            <Show when={props.description !== undefined}>
              <p class={styles.description}>{props.description}</p>
            </Show>
          </div>
          <Show when={props.actions !== undefined}>
            <div class={styles.actions}>{props.actions}</div>
          </Show>
        </header>
      </Show>
      <Show when={!folded()}>
        <div class={styles.body}>{props.children}</div>
      </Show>
    </section>
  );
};
